import request, { AxiosRequestConfig, AxiosResponse } from '@/lib/axios-config';
import { formatPriceWithoutComma } from '@/app/perps/util';
import { getApiBaseUrl, getUserToken } from './api';
import { chainIcons } from './imgs';
import { message } from 'antd';
import i18next from 'i18next';
import { formatGroupItemTitle } from '@/app/predict/utils';

const OKX_WEB3_API_BASE = getApiBaseUrl();

export interface TokenInfo {
  decimals: string | number;
  tokenContractAddress: string;
  tokenLogoUrl: string; // 币种logo
  tokenName: string; // 币种名称
  tokenSymbol: string; // 币种符号
  isMainTokenSymbol?: boolean; // 是否为主币
  balance?: string; // 余额，字符串类型，做交易使用
  usdValue?: number; // 美元价值，仅展示，不做交易，可能存在精度问题
  showBalance?: number | string; // 展示余额，仅展示，不做交易，可能存在精度问题
  showUsdValue?: number | string; // 展示美元价值，仅展示，不做交易，可能存在精度问题
  chainIndex?: number; // 链索引
  chainId?: number; // 链id
  tokenPrice?: string; // 币种价格
  mainBalance?: string; // 主币余额，字符串类型
}

export interface TokenBalance {
  chainIndex: string;
  symbol: string;
  address: string;
  tokenContractAddress: string;
  tokenPrice: string;
  isRiskToken: boolean;
  rawBalance: string;
  balance: string;
}
// 链信息接口
export interface ChainInfo {
  chainIndex: number;
  chainName: string;
  name?: string;
  chainNameForWallet: string;
  chainId: number;
  dexTokenApproveAddress: string;
  img: string;
  type: string; //只有solana和evm两种类型
  rpcUrl: string;
  mainTokenAddress: string;
  mainTokenSymbol: string;
  mainTokenDecimals: string;
  tradeDetailUrl: string;
  hashUrl: string;
  minRetainedBalance: number;
  tokens?: TokenInfo[];
  tokenList: TokenInfo[];
}

export interface BaseParams {
  chainIndex: string;
  tokenContractAddress?: string;
}

export interface KLineDataParams extends BaseParams {
  bar?: string;
  limit?: string;
  before?: string;
  after?: string;
}

export interface CandleItemData {
  originTime: number;
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  value: number;
  volume: number;
  volUsd: number;
  confirm: number;
}

// 通用请求函数 - 使用 axios
export async function baseRequest(url: string, options: AxiosRequestConfig = {}, conf?: { needRiskToast: boolean }) {
  const method = options.method || 'GET';
  const data = options.data;
  const params = options.params;
  const { needRiskToast = true } = conf || {};
  let headers: Record<string, string> = method === 'POST' ? { 'Content-Type': 'application/json' } : {};

  let userToken = getUserToken();
  // 合并用户提供的 headers
  if (options.headers) {
    Object.entries(options.headers).forEach(([key, value]) => {
      if (typeof value === 'string') {
        headers[key] = value;
      }
    });
  }
  if (userToken) {
    headers['token'] = userToken;
  }
  const config: AxiosRequestConfig = {
    method,
    url,
    headers,
    data,
    params,
    timeout: 30000, // 30秒超时
    ...options,
  };

  try {
    const response: AxiosResponse = await request(config);
    if (response.data?.code === 4031) {
      // 展示账号封禁提示
      if (needRiskToast) {
        message.error(i18next.t('riskModal.description'));
      }
      return null;
    }
    // 401 302跳走
    if (response.data?.code === 401) {
      console.log('接口401了')
    }
    return response.data;
  } catch (error: any) {
    if (error.response) {
      console.error(`请求失败: ${url}`, {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });

      // 抛出更详细的错误信息
      throw new Error(`HTTP ${error.response?.status || 'unknown'}: ${error.response?.data?.msg || error.message}`);
    } else {
      console.error(`请求失败: ${url}`, error);
      throw error;
    }
  }
}

// 1.获取 DEX 聚合器支持的链列表，手动增加对应的图片 - 暂未使用
export async function getAggregatorSupportedChains(params?: { chainIndex?: number }): Promise<ChainInfo[]> {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/okx/chains`, {
      params,
    });
    if (data.length) {
      return data.map((chain: any) => ({
        chainIndex: chain.chainIndex,
        chainName: chain.chainName,
        chainId: chain.chainId,
        dexTokenApproveAddress: chain.dexTokenApproveAddress,
        img: chainIcons[chain.chainIndex],
      }));
    }
    return [];
  } catch (error) {
    console.error('获取 DEX 聚合器支持的链失败:', error);
    return [];
  }
}

// 2.获取 DEX 支持的token列表
export async function getAggregatorAllTokens(params?: { chainIndex?: string; walletAddress?: string }): Promise<TokenInfo[]> {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/okx/tokens`, {
      params,
    });
    if (data?.length) {
      return data.map((token: any) => ({
        decimals: token.decimals,
        tokenContractAddress: token.tokenContractAddress,
        tokenLogoUrl: token.tokenLogoUrl,
        tokenName: token.tokenName,
        tokenSymbol: token.tokenSymbol,
        isMainTokenSymbol: ['0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee', '11111111111111111111111111111111'].includes(
          token.tokenContractAddress.toLowerCase()
        ),
      }));
    } else {
      return [];
    }
  } catch (error) {
    console.error('获取 Token 失败:', error);
    return [];
  }
}

export async function getAllTokens(params?: { chainId?: string; walletAddress?: string }): Promise<TokenInfo[]> {
  try {
    const { records = [] } = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/tokens`, {
      params,
    });
    if (records?.length) {
      // 找到当前链下主币的余额，并记录到token下
      const mainToken = records.find(
        (item: any) =>
          ['0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee', '11111111111111111111111111111111'].includes(item.address.toLowerCase()) || !item.address
      );

      records.forEach((item: any) => {
        // 转换成token组件数据格式
        item.tokenContractAddress = item.address;
        item.tokenName = item.name;
        item.tokenSymbol = item.symbol;
        item.tokenLogoUrl = item.icon;
        item.isMainTokenSymbol =
          ['0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee', '11111111111111111111111111111111'].includes(item.address.toLowerCase()) || !item.address;
        item.chainId = Number(params?.chainId);
        // 兼容历史逻辑，请使用chainId，chainIndex已经废弃
        item.chainIndex = Number(params?.chainId);
        item.showBalance = formatPriceWithoutComma({ price: item?.balance || 0, decimal: 6, defaultZero: '0.00' });
        item.showUsdValue = formatPriceWithoutComma({ price: item?.usdValue || 0, decimal: 2, defaultZero: '0.00' });
        item.mainBalance = formatPriceWithoutComma({ price: mainToken?.balance || 0 || 0, decimal: 6, defaultZero: '0.00' });
      });
    }
    return records;
  } catch (error) {
    console.error('获取 Token 失败:', error);
    return [];
  }
}

export async function getConvertTokens(params?: { chainId?: string; walletAddress?: string }): Promise<TokenInfo[]> {
  try {
    const { records = [] } = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/hyperliquid/tokens`, {
      params,
    });
    if (records?.length) {
      records.forEach((item: any) => {
        // 转换成token组件数据格式
        item.tokenContractAddress = item.address;
        item.tokenName = item.name;
        item.tokenSymbol = item.symbol;
        item.tokenLogoUrl = item.icon;
        item.isMainTokenSymbol = ['0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee', '11111111111111111111111111111111'].includes(
          item.address.toLowerCase()
        );
        item.chainId = Number(params?.chainId);
        // 兼容历史逻辑，请使用chainId，chainIndex已经废弃
        item.chainIndex = Number(params?.chainId);
        item.showBalance = formatPriceWithoutComma({ price: item?.balance || 0, decimal: 6, defaultZero: '0.00' });
        item.showUsdValue = formatPriceWithoutComma({ price: item?.usdValue || 0, decimal: 2, defaultZero: '0.00' });
      });
    }
    return records;
  } catch (error) {
    console.error('获取 Token 失败:', error);
    return [];
  }
}

// 3.获取token价格
export async function batchGetTokenPriceInfo(params: BaseParams[]) {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/api/v5/dex/market/price-info`, { method: 'POST', data: params });
    return data?.length ? data : [];
  } catch (error) {
    return [];
  }
}
// 4.获取K线数据
export async function getOkxKlineData(params: KLineDataParams) {
  try {
    const resp = await baseRequest('/app/dex/okx/kline', {
      params: { limit: '100', ...params },
    });
    return resp;
  } catch (error) {
    return [];
  }
}
// 4.获取K线数据
export async function getKlineData(params: KLineDataParams) {
  try {
    // const resp = await baseRequest(
    //   `${OKX_WEB3_API_BASE}/api/v5/dex/market/candles`,
    //   { params: { ...rest, limit } }
    // );
    const resp = await baseRequest('/app/dex/okx/kline', {
      params: { limit: '100', ...params },
    });
    const data: CandleItemData[] = [];
    // 倒序遍历 resp，从最新到最旧
    for (let i = resp.length - 1; i >= 0; i--) {
      const bar = resp[i];
      data.push({
        // 转成东八区的秒级时间戳
        originTime: Number(bar[0]),
        time: Number(bar[0].slice(0, -3)) + 28800,
        open: Number(bar[1]),
        high: Number(bar[2]),
        low: Number(bar[3]),
        close: Number(bar[4]),
        value: Number(bar[5]),
        volume: Number(bar[5]),
        volUsd: Number(bar[6]),
        confirm: Number(bar[7]),
      });
    }
    return data;
  } catch (error) {
    return [];
  }
}

// 5.获取token兑换价格
export async function getAggregatorQuota(params: any): Promise<any> {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/swap/quote`, {
      params,
    });
    return data;
  } catch (error: any) {
    console.error('获取 OKX 价格失败:', error);
    return error?.data?.msg || '';
  }
}

// 6.swap交易
export async function swapToken(params: any, t?: any) {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/swap/swap`, {
      params,
    });
    if (data?.length) {
      return data;
    } else {
      let _msg = data?.msg;
      if (t && _msg === 'Insufficient liquidity') {
        // 流动性不足
        _msg = t('swapError.insufficientLiquidity');
      } else if (t && _msg.startsWith('The value difference from this transaction’s quote route is higher than')) {
        // 价格影响过大
        _msg = t('swapError.differenceTooLarge');
      }
      return {
        code: data?.code,
        msg: _msg,
      };
    }
  } catch (error) {
    console.error('获取 OKX 价格失败:', error);
    return null;
  }
}

// 7.模拟交易
export async function simulate(params: any) {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/transaction/simulate`, { method: 'POST', data: params });
    return data?.length ? data : [];
  } catch (error) {
    return [];
  }
}

// 8.广播交易
export async function broadcast(params: any) {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/transaction/broadcast`, { method: 'POST', data: params });
    return data?.length ? data : [];
  } catch (error) {
    return [];
  }
}

// 9.追踪交易
export async function transactionStatus(params: any) {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/transaction/orders`, { params });
    return data?.length ? data : [];
  } catch (error) {
    return [];
  }
}

// 获取token详情
export async function getTokenDetail(params: any) {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/market/token/overview`, { params });
    return data;
  } catch (error) {
    return [];
  }
}

// 获取余额
export async function getBalancesByAddress(params: any): Promise<TokenBalance[]> {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/balance/list-token-balances`, { params });
    // const data = [
    //   {
    //     tokenAssets: [
    //       {
    //         chainIndex: '42161',
    //         symbol: 'ETH',
    //         address: '0x7d8d4ec55d6213000285376fa512fe778f228e34f',
    //         balance: '0.09799722348',
    //         tokenContractAddress: '',
    //         tokenPrice: '3977.4',
    //         isRiskToken: false,
    //         rawBalance: '979972234800000000',
    //       },
    //       {
    //         chainIndex: '42161',
    //         symbol: 'USDC',
    //         address: '0x7d8d4ec55d6213000285376fa512fe778f228e34f',
    //         balance: '8.284878',
    //         tokenContractAddress: '0xaf88d065e77c8cc22389327c5edb3a432268e5831',
    //         tokenPrice: '0.9997',
    //         isRiskToken: false,
    //         rawBalance: '8284878',
    //       },
    //     ],
    //   },
    // ];
    return data?.length ? data[0]?.tokenAssets || [] : [];
  } catch {
    return [];
  }
}

export async function getApproveTransaction(params: any, conf?: { needRiskToast: boolean }) {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/swap/approve_transaction`, { params }, conf);
    return data?.length ? data[0] : {};
  } catch (error) {
    return {};
  }
}
export async function getGasLimit(params: any) {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/transaction/gas-limit`, { method: 'POST', data: params });
    if (data?.[0]?.gasLimit) {
      return data[0].gasLimit;
    }
    return '';
  } catch (error) {
    return '';
  }
}

export async function getGasPrice(params: any) {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/transaction/gas-price`, { params });
    return data?.length ? data[0] : {};
  } catch (error) {
    return {};
  }
}

// 交易活动
export async function getTradeActivity(params: any) {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/market/trading-history`, { params });
    return data?.length ? data : [];
  } catch (error) {
    return [];
  }
}

// 交易详情
export async function getSwapHistory(params: any) {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/swap/history`, { params });
    return data || null;
  } catch (error) {
    return null;
  }
}

// 交易详情
export async function getMarketRanking(params: any) {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/market/ranking`, { params });
    return data || null;
  } catch (error) {
    return null;
  }
}

// 交易上报
export async function transactionReport(params: any) {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/transaction/report`, { method: 'POST', data: params });
    return data || null;
  } catch (error) {
    return null;
  }
}

// 交易上报
export async function transactionHistory(params: any) {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/transaction/user_history`, { method: 'POST', data: params });
    return data || null;
  } catch (error) {
    return null;
  }
}

// token详情
export async function getTokenInfo(params: any) {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/token`, {
      params,
    });
    return data || null;
  } catch (error) {
    return null;
  }
}

// 用户的交易记录
export async function getUserTransactionHistory(params: any): Promise<{
  list: any[];
  chainId?: number;
  currPage: number;
  totalPage: number;
  totalCount: number;
  pageSize: number;
}> {
  const defaultData = {
    list: [],
    totalPage: 1,
    currPage: 1,
    totalCount: 0,
    pageSize: 10,
  };
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/transaction/user_history_with_login`, { method: 'POST', data: params });
    return data || defaultData;
  } catch (error) {
    return defaultData;
  }
}

// 获取合约跟单交易记录
export async function getPerpsTransactionHistory(params: any) {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/transaction/hyperliquid_order`, { params });
    return data || null;
  } catch (error) {
    return null;
  }
}
export async function getEstimateReserveGasTokenFee(params: any) {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/swap/estimateReserveGasTokenFee`, { params });
    return data;
  } catch (error) {
    return null;
  }
}

// 获取返佣概览
export async function getReferralOverview() {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/commission/overview`, {});
    return data || null;
  } catch (error) {
    return null;
  }
}

// 获取返佣图表数据
export async function getReferralChartData(params: any) {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/commission/chart-data`, { params });
    return data || null;
  } catch (error) {
    return null;
  }
}

// 获取返佣记录
export async function getCommissionRecord(params: any) {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/commission/records/query`, { method: 'POST', data: params });
    return data || null;
  } catch (error) {
    return null;
  }
}

// 佣金提现记录
export async function getWithdrawRecord(params: any) {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/commission/withdrawal/records`, { params });
    return {
      list: data.records,
      totalCount: data.total,
      totalPage: data.pages,
      currPage: data.current,
      pageSize: data.size,
    };
  } catch (error) {
    return {
      list: [],
      totalPage: 1,
      currPage: 1,
      totalCount: 0,
      pageSize: 10,
    };
  }
}

// 设置返佣折扣率
export async function setDiscountRate(params: any) {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/commission/discount-rate`, { method: 'POST', data: params });
    return data || null;
  } catch (error) {
    return null;
  }
}

// 获取可提现余额
export async function getWithdrawalBalance(params: any): Promise<any[]> {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/commission/withdrawal/balance`, { params });
    return data || [];
  } catch (error) {
    return [];
  }
}

// 提现
export async function referralWithdraw(params: any) {
  try {
    const data = await baseRequest(`${OKX_WEB3_API_BASE}/app/commission/withdrawal/create`, { method: 'POST', data: params });
    return data || null;
  } catch (error) {
    return null;
  }
}

// 询价接口，现货转合约
export const getConvertQuote = async (params: { srcChainId: number; dstChainId: number; srcToken: string; dstToken: string; srcAmount: string }) => {
  try {
    const data: any = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/hyperliquid/quote`, {
      method: 'POST',
      data: params,
    });
    return data || null;
  } catch (error) {
    return null;
  }
};

// 划转 - 现货到合约
export const convertExecute = async (params: { fromChainId: number; fromTokenAddress: string; amount: string }) => {
  try {
    const data: any = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/hyperliquid/transfer`, {
      method: 'POST',
      data: params,
    });
    return data || null;
  } catch (error) {
    return null;
  }
};

// 划转/提现 - 合约提现（合约到现货）
export const perpsWithdraw = async (params: any) => {
  try {
    const data: any = await baseRequest(`${OKX_WEB3_API_BASE}/app/hyperliquid/withdraw`, {
      method: 'POST',
      data: params,
    });
    console.log(data);
    return data;
  } catch (error) {
    return null;
  }
};

// 现货提现试算-gas费计算
export const getSpotTransferFee = async (params: any) => {
  try {
    const data: any = await baseRequest(`${OKX_WEB3_API_BASE}/app/spot/withdrawal/estimate-gas`, {
      method: 'POST',
      data: params,
    });
    return data || null;
  } catch (error) {
    return null;
  }
};

// 现货提现
export const spotWithdraw = async (params: any) => {
  try {
    const data: any = await baseRequest(`${OKX_WEB3_API_BASE}/app/spot/withdrawal/create`, {
      method: 'POST',
      data: params,
    });
    return data || null;
  } catch (error) {
    return null;
  }
};

// 现货 -> 现货划转
export const spotExchange = async (params: any) => {
  try {
    const data: any = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/hyperliquid/exchange`, {
      method: 'POST',
      data: params,
    });
    return data || null;
  } catch (error) {
    return null;
  }
};

// 现货提现上报记录
export const reportSpotWithdraw = async (params: any) => {
  try {
    const data: any = await baseRequest(`${OKX_WEB3_API_BASE}/app/spot/withdrawal/report`, {
      method: 'POST',
      data: params,
    });
    return data || null;
  } catch (error) {
    return null;
  }
};

// 获取主币的最大余额
export const getMaxSwapAmount = async (params: any) => {
  try {
    const data: any = await baseRequest(`${OKX_WEB3_API_BASE}/app/dex/swap/getMaxSwapAmount`, {
      params,
    });
    return data || null;
  } catch (error) {
    return null;
  }
};

export interface PredictionCarouselList {
  eventId: string; // 事件ID
  endDate: string; // 事件结束时间
  eventData: string; // 事件数据
  image: string; // 事件图片
  options: PredictionCarouselOption[]; // 事件选项
  tags: string[]; // 事件标签
  slug: string; // 事件slug
  source: string; // 事件来源
  title: string; // 事件标题
  volume: number; // 事件成交量
}

export interface PredictionCarouselOption {
  icon: string;
  slug: string;
  id: number;
  outcomePrices: string; // "[\"0.0115\", \"0.9885\"]"
  groupItemTitle: string;
  closed: boolean;
  volume: string;
  clobTokenIds: string;
  question: string;
}

// ========== Prediction ==========
// 轮播图热榜数据（GET /app/prediction/hot/carousel）
export const getPredictionHotCarousel = async (params: { language: string }): Promise<PredictionCarouselList[] | []> => {
  try {
    const data: any = await baseRequest(`${OKX_WEB3_API_BASE}/app/prediction/hot/carousel`, { params });
    if (data?.events?.length) {
      return (data.events as PredictionCarouselList[]).map(item => ({
        ...item,
        options: item?.options?.slice(0, 4).map(option => ({
          ...option,
          groupItemTitle: formatGroupItemTitle(option.groupItemTitle || ''),
        })),
        // options: [
        //   {
        //     icon: '',
        //     closed: false,
        //     outcomePrices: '["0.2765", "0.7235"]',
        //     groupItemTitle: '加文·纽瑟姆',
        //     question: '加文·纽瑟姆会赢得2028年民主党总统候选人提名吗？',
        //     slug: '加文·纽瑟姆',
        //     clobTokenIds:
        //       '["54533043819946592547517511176940999955633860128497669742211153063842200957669", "87854174148074652060467921081181402357467303721471806610111179101805869578687"]',
        //     id: 559652,
        //     volume: '20551063.175797593',
        //   },
        //   {
        //     icon: '',
        //     closed: false,
        //     outcomePrices: '["0.0835", "0.9165"]',
        //     groupItemTitle: '分组项标题：亚历山德里亚·奥卡西奥-科尔特斯',
        //     question: '问题：亚历山德里亚·奥卡西奥-科尔特斯会赢得2028年民主党总统提名吗？',
        //     slug: '分组项标题：亚历山德里亚·奥卡西奥-科尔特斯',
        //     clobTokenIds:
        //       '["107064985435494333113391038470401719113272800530429703182710416066774068907304", "65176072261324737856085688071627118509549293922582857186996392180609764586527"]',
        //     id: 559653,
        //     volume: '9667484.084764212',
        //   },
        //   {
        //     icon: '',
        //     closed: false,
        //     outcomePrices: '["0.059", "0.941"]',
        //     groupItemTitle: '乔恩·奥索夫',
        //     question: '乔恩·奥索夫会赢得2028年的民主党总统提名吗？',
        //     slug: '乔恩·奥索夫',
        //     clobTokenIds:
        //       '["70071592420137476676935286377781779672157004436137616627487590484756055232944", "107829893465244243112531790315844862173967342141753896250561816305880797209350"]',
        //     id: 559661,
        //     volume: '7702754.505681931',
        //   },
        //   {
        //     icon: '',
        //     closed: false,
        //     outcomePrices: '["0.036", "0.964"]',
        //     groupItemTitle: '皮特·布蒂吉格',
        //     question: '皮特·布蒂吉格会赢得2028年民主党总统提名吗？',
        //     slug: '皮特·布蒂吉格',
        //     clobTokenIds:
        //       '["91031279171981959197361710127213577102576826515163085396576756946418341946256", "18494065527924073750217237765784380365430319396356251245973222791140100738948"]',
        //     id: 559654,
        //     volume: '9816879.952672733',
        //   },
        // ],
      }));
    }
    return [];
  } catch (error) {
    return [];
  }
};

export interface PredictionNewsHeadlines {
  link: string; // 新闻链接
  pubDate: string; // 新闻发布时间
  title: string; // 新闻标题
  pubTimestamp: number; // 新闻发布时间戳
}

// 突发新闻（GET /app/prediction/news/headlines）
export const getPredictionNewsHeadlines = async (params: { language: string }): Promise<PredictionNewsHeadlines[] | []> => {
  try {
    const data: any = await baseRequest(`${OKX_WEB3_API_BASE}/app/prediction/news/headlines`, { params });
    if (data?.length) {
      return data;
    }
    return [];
  } catch (error) {
    return [];
  }
};

export interface PredictionMarqueeNews {
  title: string; // 新闻标题
  link: string; // 新闻链接
  publicationName: string; // 新闻来源
  publicationFavicon: string; // 新闻来源图标
  pubDate: string; // 新闻发布时间
  pubTimestamp: number; // 毫秒级时间戳，单位：毫秒
  sentiment?: string; // 利好/利空：bullish | bearish
}

export type PredictionMarqueeNewsMap = Record<string, PredictionMarqueeNews[]>;

// 按事件ID获取关联新闻
export const getPredictionNews = async (params: { eventIds: string[]; language: string }): Promise<PredictionMarqueeNewsMap> => {
  try {
    const data: any = await baseRequest(`${OKX_WEB3_API_BASE}/app/prediction/news`, {
      method: 'POST',
      data: params,
    });
    if (data && typeof data === 'object') {
      return data;
    }
    return {};
  } catch (error) {
    return {};
  }
};
